// src/components/AssetCard/AssetCard.tsx
import React, { useState } from 'react';
import type { PersonFile } from '@/services/homeService';
import './AssetCard.css';

interface AssetCardProps {
  file: PersonFile;
  onClickThumbnail?: (file: PersonFile) => void;
  onClickTitle?: (conversationId: number) => void;
}

/**
 * Reusable image card component for asset galleries.
 * Matches the legacy AngularJS `project-files-item` structure.
 */
export const AssetCard: React.FC<AssetCardProps> = ({ file, onClickThumbnail, onClickTitle }) =>
{
  const [imageError, setImageError] = useState(false);
  const thumbnailUrl = file.Thumbnail?.FileUrl;

  return (
    <div
      className="project-files-item"
      data-conversationid={file.ConversationId}
      data-attachmentgalleryid={file.AttachmentId || 0}
    >
      {/* Thumbnail area */}
      <div className="project-files-item-details">
        <div className="project-files-item-image">
          {thumbnailUrl && !imageError ? (
            <div
              className="project-files-item-thumbnail"
              style={{ backgroundImage: `url(${thumbnailUrl})` }}
              onClick={() => onClickThumbnail?.(file)}
              onKeyDown={e => e.key === 'Enter' && onClickThumbnail?.(file)}
              role="button"
              tabIndex={0}
            >
              {/* Hidden img to detect load errors */}
              <img
                src={thumbnailUrl}
                alt=""
                style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
                onError={() => setImageError(true)}
              />
            </div>
          ) : (
            <div
              className="project-files-item-thumbnail"
              style={{ backgroundColor: '#f6f6f6' }}
            ></div>
          )}
        </div>
      </div>

      {/* Card info */}
      <div className="project-files-item-card">
        {/* Layer tags (max 3) */}
        <div className="project-files-item-card-layers">
          {file.Layers?.slice(0, 3).map(layer => (
            <span
              key={`${layer.ConversationId}-${layer.LayerId}`}
              className="layer-tag"
              data-layerid={layer.LayerId}
              style={{ backgroundColor: layer.LayerColor }}
            />
          ))}
        </div>

        {/* Icon */}
        <div className="project-files-item-card-icon">
          <i className={file.Icon} />
        </div>

        {/* Title */}
        <div
          className="project-files-item-card-name"
          onClick={() => onClickTitle?.(file.ConversationId)}
          onKeyDown={e => e.key === 'Enter' && onClickTitle?.(file.ConversationId)}
          role="button"
          tabIndex={0}
        >
          {file.Title}
        </div>
      </div>
    </div>
  );
};
